import React from "react";
import InspectorPanel from "../InspectorPanel";

const HOBBLIVISION_DEBUG = false;

export default function CandidatePage({
    candidates = [],
    scanResult,
    onSelect,
    onBack,
    onManualEntry,
}) {
    const matchResult =
        scanResult?.matchResult || null;

    const parsedResult =
        scanResult?.parsedResult || null;

    /*
     * ============================================================
     * HELPERS
     * ============================================================
     */

    const formatConfidence = (value) => {
        if (value === null || value === undefined) {
            return 0;
        }

        const numeric = Number(value);

        if (Number.isNaN(numeric)) {
            return 0;
        }

        return numeric <= 1
            ? Math.round(numeric * 100)
            : Math.round(numeric);
    };

    const getConfidenceLabel = (confidence) => {
        if (confidence >= 80) {
            return "Strong possible match";
        }

        if (confidence >= 50) {
            return "Possible match";
        }

        return "Low-confidence match";
    };

    const getItemValue = (item, ...keys) => {
        for (const key of keys) {
            if (
                item?.[key] !== undefined &&
                item?.[key] !== null &&
                item?.[key] !== ""
            ) {
                return item[key];
            }
        }

        return null;
    };

    /*
     * ============================================================
     * PARSER SUMMARY
     * ============================================================
     */

    const detectedName =
    parsedResult?.player ||
    parsedResult?.itemName ||
    null;

    const resolvedName =
        scanResult?.matchResult?.resolvedNameEvidence
            ?.primary?.value ||
        null;

    const detectedSet =
        parsedResult?.set ||
        null;

    const detectedCardNumber =
        parsedResult?.cardNumber ||
        null;

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <div className="hv-page">

            {/* ====================================================
                INSPECTOR
            ===================================================== */}

            {HOBBLIVISION_DEBUG && scanResult && (
                <InspectorPanel
                    originalImage={
                        scanResult.processedImage?.imageUrl
                    }
                    processedImage={
                        scanResult.processedImage?.imageUrl
                    }
                    extractedCandidates={
                        scanResult.extractedCandidates
                    }
                    metadata={
                        scanResult.inspector?.metadata
                    }
                    pipeline={
                        scanResult.inspector?.pipeline
                    }
                    detection={
                        scanResult.detection
                    }
                    regions={
                        scanResult.regions
                    }
                    textRegions={
                        scanResult.textRegions
                    }
                    roi={
                        scanResult.roi
                    }
                    ocrPreprocessing={
                        scanResult.ocrPreprocessed
                    }
                />
            )}

            {/* ====================================================
                HEADER
            ===================================================== */}

            <div className="hv-welcome-icon">
                🎯
            </div>

            <h2 className="hv-page-title">
                Possible Matches
            </h2>

            <p className="hv-page-description">
                HobbliVision found these possible matches.
                Select the one that looks correct.
            </p>

            {/* ====================================================
                DETECTED CLUES
            ===================================================== */}

            {(detectedName ||
                detectedSet ||
                detectedCardNumber) && (

                <div className="hv-detected-summary">

                    <div className="hv-detected-summary-title">
                        Detected from image
                    </div>

                    <div className="hv-detected-clues">

                        {detectedName && (
                            <div className="hv-detected-clue">
                                <span className="hv-clue-label">
                                    Detected Name:{' '}
                                </span>

                                <span className="hv-clue-value">
                                    {detectedName}
                                </span>
                            </div>
                        )}

                        {resolvedName &&
                            resolvedName !== detectedName && (
                                <div className="hv-detected-clue">
                                    <span className="hv-clue-label">
                                        Normalized Name:{' '}
                                    </span>

                                    <span className="hv-clue-value">
                                        {resolvedName}
                                    </span>
                                </div>
                            )}

                        {detectedSet && (
                            <div className="hv-detected-clue">
                                <span className="hv-clue-label">
                                    Set: {' '}
                                </span>

                                <span className="hv-clue-value">
                                    {detectedSet}
                                </span>
                            </div>
                        )}

                        {detectedCardNumber && (
                            <div className="hv-detected-clue">
                                <span className="hv-clue-label">
                                    Card #: {' '}
                                </span>

                                <span className="hv-clue-value">
                                    {detectedCardNumber}
                                </span>
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* ====================================================
                MATCHER SUMMARY
            ===================================================== */}

            {matchResult && (
                <div className="hv-match-summary">

                    <div className="hv-match-summary-title">
                        Match Results
                    </div>

                    <div className="hv-match-summary-details">

                        <span>
                            {candidates.length} possible{" "}
                            {candidates.length === 1
                                ? "match"
                                : "matches"}
                        </span>

                        {matchResult.confidence !== undefined && (
                            <span>
                                Overall confidence:{" "}
                                {formatConfidence(
                                    matchResult.confidence
                                )}%
                            </span>
                        )}

                    </div>

                </div>
            )}

            {/* ====================================================
                CANDIDATE LIST
            ===================================================== */}

            {candidates.length > 0 ? (

                <div className="hv-candidate-list">

                    {candidates.map((candidate) => {

                        /*
                         * Current matcher structure:
                         *
                         * candidate = {
                         *     id,
                         *     item,
                         *     itemName,
                         *     score,
                         *     confidence,
                         *     reasons,
                         *     scoreBreakdown
                         * }
                         */

                        const item =
                            candidate.item ||
                            candidate;

                        const categoryId =
                            getItemValue(
                                item,
                                "category_id",
                                "categoryId"
                            );

                        const subcategoryId =
                            getItemValue(
                                item,
                                "subcategory_id",
                                "subcategoryId"
                            );
                        
                        const name =
                            candidate.itemName ||
                            getItemValue(
                                item,
                                "name",
                                "itemName",
                                "user_specific_name",
                                "userSpecificName"
                            ) ||
                            "Unknown Item";

                        const year =
                            getItemValue(
                                item,
                                "year",
                                "releaseYear",
                                "cardYear",
                                "user_specific_year"
                            );

                        const brand =
                            getItemValue(
                                item,
                                "brand",
                                "brand_name",
                                "user_specific_brand",
                                "userSpecificBrand"
                            );

                        const set =
                            getItemValue(
                                item,
                                "set_name",
                                "set",
                                "user_specific_set_name",
                                "userSpecificSetName"
                            ) || parsedResult?.set;

                        const player =
                            getItemValue(
                                item,
                                "player",
                                "player_name"
                            );

                        const number =
                            getItemValue(
                                item,
                                "card_number",
                                "number",
                                "user_specific_card_number",
                                "cardNumber"
                            );

                        const databaseImage =
                            item.databaseImages?.find(
                                (image) => image?.isMain
                            ) ||
                            item.databaseImages?.[0];

                        const image =
                            databaseImage?.url ||
                            getItemValue(
                                item,
                                "image_url",
                                "imageUrl",
                                "image"
                            );


                        const confidence =
                            formatConfidence(
                                candidate.confidence ??
                                candidate.score
                            );

                        return (
                            <button
                                key={
                                    candidate.id ||
                                    item.id
                                }
                                className="hv-candidate-card"
                                onClick={() =>
                                    onSelect(
                                        item
                                    )
                                }
                            >

                                {/* ============================
                                    IMAGE
                                ============================= */}

                                <div className="hv-candidate-image-wrapper">

                                    {image ? (
                                        <img
                                            src={image}
                                            alt={name}
                                            className="hv-candidate-image"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="hv-candidate-image-placeholder">
                                            🃏
                                        </div>
                                    )}

                                </div>

                                {/* ============================
                                    INFORMATION
                                ============================= */}

                                <div className="hv-card-info">

                                    <div className="hv-card-title">
                                        {name}
                                    </div>

                                    {(year || brand) && (
                                        <div className="hv-card-subtitle">
                                            {[
                                                year,
                                                brand
                                            ]
                                                .filter(Boolean)
                                                .join(" • ")}
                                        </div>
                                    )}

                                    {set && (
                                        <div className="hv-card-set">
                                            {set}
                                        </div>
                                    )}

                                    {player &&
                                        player !== name && (
                                            <div className="hv-player">
                                                {player}
                                            </div>
                                        )}

                                    {number && (
                                        <div className="hv-card-number">
                                            Card #{number}
                                        </div>
                                    )}

                                    {/* ========================
                                        MATCH REASONS
                                    ========================= */}

                                    {candidate.reasons?.length > 0 && (
                                        <div className="hv-match-reasons">

                                            {candidate.reasons.map(
                                                (
                                                    reason,
                                                    index
                                                ) => (
                                                   <div key={index} className="hv-match-reason">
                                                        <span className="hv-match-reason-check">
                                                            ✓
                                                        </span>
                                                        <span>
                                                            {reason}
                                                        </span>
                                                    </div>
                                                )
                                            )}

                                        </div>
                                    )}

                                </div>

                                {/* ============================
                                    CONFIDENCE
                                ============================= */}

                                <div className="hv-candidate-right">

                                    <div className="hv-confidence">
                                        {confidence}%
                                    </div>

                                    <div className="hv-confidence-label">
                                        {getConfidenceLabel(
                                            confidence
                                        )}
                                    </div>

                                    <div className="hv-select-indicator">
                                        Select →
                                    </div>

                                </div>

                            </button>
                        );
                    })}

                </div>

            ) : (

                <div className="hv-no-candidates">
                    <div className="hv-no-candidates-icon">
                     🔍
                    </div>
                    <div className="hv-no-candidates-title">
                        No possible matches found
                    </div>
                    <div className="hv-no-candidates-description">
                        HobbliVision couldn't find a
                        suitable database match for this item.
                    </div>
                </div>
            )}

            {/* ====================================================
                ACTIONS
            ===================================================== */}

            <div className="hv-candidate-actions">
                <button
                    className="hv-secondary-button"
                    onClick={onBack}
                >
                    Scan Again
                </button>
                <button
                    className="hv-secondary-button"
                    onClick={onManualEntry}
                >
                    None of these — Enter Manually
                </button>
            </div>
        </div>
    );
}

